import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { Link } from '@inertiajs/react'
import type { WeatherFull } from '@/types/weather'
import { WeatherIcon } from '@/lib/icons'

interface Props {
  weather: WeatherFull | null
  has_location: boolean
}


const ADVISORY_LABELS: Record<string, { text: string; color: string }> = {
  reduce: { text: 'Ogranicz podlewanie', color: 'text-botanical' },
  normal: { text: 'Podlewanie normalne', color: 'text-ink' },
  increase: { text: 'Zwiększ podlewanie', color: 'text-gold' },
}

function todayFormatted(): string {
  return new Date().toLocaleDateString('pl-PL', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

function dayName(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pl-PL', { weekday: 'short' })
}

function fullDayName(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })
}

function WeatherIndex({ weather, has_location }: Props) {
  if (!has_location) {
    return (
      <AtlasPage>
        <PageTitle title="Pogoda" />
        <Separator />
        <p className="text-center text-frame italic">
          Ustaw lokalizację w{' '}
          <Link href="/settings" className="text-botanical underline">ustawieniach</Link>
          , aby widzieć prognozę pogody.
        </p>
        <PageNumber number={42} />
      </AtlasPage>
    )
  }

  if (!weather) {
    return (
      <AtlasPage>
        <PageTitle title="Pogoda" />
        <Separator />
        <p className="text-center text-frame italic">
          Nie udało się pobrać danych pogodowych. Spróbuj ponownie później.
        </p>
        <PageNumber number={42} />
      </AtlasPage>
    )
  }

  return (
    <AtlasPage>
      <PageTitle title="Pogoda" subtitle={`${weather.city_name} \u00b7 ${todayFormatted()}`} />
      <Separator />

      {/* Current conditions */}
      <div className="grid grid-cols-2 gap-md md:grid-cols-4">
        <div className="text-center flex flex-col items-center">
          <div className="h-[2.25rem] flex items-center">
            <WeatherIcon code={weather.current.weather_code} isDay={weather.current.is_day} size={32} />
          </div>
          <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
            {weather.current.weather_description}
          </p>
        </div>
        <div className="text-center">
          <p className="font-display text-2xl">{Math.round(weather.current.temperature)}°C</p>
          <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
            Temperatura
          </p>
        </div>
        <div className="text-center">
          <p className="font-display text-2xl">{weather.current.humidity}%</p>
          <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
            Wilgotność
          </p>
        </div>
        <div className="text-center">
          <p className="font-display text-2xl">
            {weather.plant_impact.uv_peak_today != null
              ? weather.plant_impact.uv_peak_today.toFixed(1)
              : '\u2014'}
          </p>
          <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
            UV maks.
          </p>
        </div>
      </div>

      {/* Plant impact */}
      <div className="mt-md border border-frame/30 bg-paper-dark px-md py-sm text-center">
        {(() => {
          const advisory = ADVISORY_LABELS[weather.plant_impact.watering_advisory] || ADVISORY_LABELS.normal
          return (
            <p className={`font-label text-sm ${advisory.color}`}>
              {advisory.text}
              {weather.plant_impact.advisory_reason && (
                <span className="text-frame italic"> — {weather.plant_impact.advisory_reason}</span>
              )}
            </p>
          )
        })()}
        {weather.plant_impact.frost_warning && (
          <p className="font-label text-xs text-danger mt-xs">
            Uwaga: prognozowane temperatury poniżej 5°C
          </p>
        )}
        {weather.plant_impact.heat_warning && (
          <p className="font-label text-xs text-danger mt-xs">
            Uwaga: prognozowane temperatury powyżej 35°C
          </p>
        )}
      </div>

      {/* 7-day forecast */}
      <Separator />
      <h2 className="font-display text-base text-center uppercase tracking-wider">
        Prognoza 7-dniowa
      </h2>

      <div className="mt-md space-y-xs">
        {weather.daily.map((day) => (
          <div key={day.date} className="flex items-center justify-between px-sm py-xs border-b border-frame/10 last:border-0">
            <span className="font-label text-sm text-ink w-32 capitalize">{fullDayName(day.date)}</span>
            <WeatherIcon code={day.weather_code} size={20} />
            <span className="font-label text-sm text-ink w-16 text-right">
              {Math.round(day.temp_max)}° <span className="text-frame">{Math.round(day.temp_min)}°</span>
            </span>
            <span className="font-label text-xs text-frame w-14 text-right">
              {day.precipitation_probability}%
            </span>
            <span className="font-label text-xs text-frame w-14 text-right">
              UV {day.uv_index_max?.toFixed(0) ?? '\u2014'}
            </span>
          </div>
        ))}
      </div>

      {/* Hourly forecast */}
      {weather.hourly.length > 0 && (
        <>
          <Separator />
          <h2 className="font-display text-base text-center uppercase tracking-wider">
            Prognoza godzinowa
          </h2>

          <div className="mt-md overflow-x-auto">
            <div className="flex gap-md min-w-max px-sm">
              {weather.hourly.map((h) => (
                <div key={h.time} className="text-center min-w-[3rem]">
                  <p className="font-label text-xs text-frame">{h.time}</p>
                  <p className="font-display text-sm mt-xs">{Math.round(h.temperature)}°</p>
                  <p className="font-label text-xs text-frame mt-xs">{h.humidity}%</p>
                  {h.precipitation_probability > 0 && (
                    <p className="font-label text-xs text-botanical mt-xs">{h.precipitation_probability}%</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Separator />
      <div className="flex items-center justify-center">
        <Button href="/" variant="secondary">Powrót</Button>
      </div>

      <PageNumber number={42} />
    </AtlasPage>
  )
}

WeatherIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default WeatherIndex
